import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Iconify, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import CloseIcon from '@mui/icons-material/Close';
import { FileUploader } from 'react-drag-drop-files';
import { useSnackbar } from 'notistack';
import { generatePreSignedUrl } from 'apps/t2c-fe/services/upload/generatePreSignedUrl';
import { usePresignedUrlUpload, useUploadId } from 'apps/t2c-fe/services';
import { CustomPlayer } from '../video-player/CustomPlayer';
import { extractFileMetadata } from 'apps/t2c-fe/services/upload/extractFileMetadata';

interface UploadVideoProps {
  uploadURL: string;
  openBrowseBRollModal: () => void;
  handleUpdateImage: (id: string, url: string, duration?: number) => void;
  abortController: React.MutableRefObject<any>;
  uploading: boolean;
  setUploading: React.Dispatch<React.SetStateAction<boolean>>;
  limit: number;
  isFullPage?: boolean;
  durationLimit?: number;
  preValidator?: (file: File) => Promise<void>;
  uploadBackgroundColor?: string;
}

export const UploadVideo: React.FC<UploadVideoProps> = ({
  uploadURL,
  openBrowseBRollModal,
  handleUpdateImage,
  abortController,
  uploading,
  setUploading,
  limit,
  isFullPage = false,
  durationLimit = 0,
  preValidator,
  uploadBackgroundColor,
}) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: uploadToPresignedUrl } = usePresignedUrlUpload({});
  const { mutateAsync: fetchUploadId } = useUploadId();

  const handleFileSelect = async (file) => {
    if (file.size > limit * 1048576) {
      enqueueSnackbar(`File size exceeds ${limit}MB . Please choose a smaller file.`, {
        variant: 'error',
      });
      return;
    }
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';

    // Run pre-validator if provided
    if (preValidator) {
      try {
        await preValidator(file);
      } catch (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
    }

    videoElement.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoElement.src);
      const filePreview = URL.createObjectURL(file);
      if (durationLimit && videoElement.duration > durationLimit) {
        enqueueSnackbar(`Duration of video should be less than ${durationLimit} seconds`, {
          variant: 'error',
        });
        return;
      }
      if (isFullPage && (videoElement.duration < 3 || videoElement.duration > 30)) {
        enqueueSnackbar(`Duration of video should be between 3 to 30 seconds`, {
          variant: 'error',
        });
        return;
      }
      handleUpdateImage('', filePreview, Math.round(videoElement.duration));
      handleFileUpload(file, filePreview, Math.round(videoElement.duration));
    };

    videoElement.src = URL.createObjectURL(file);
  };

  const handleFileUpload = async (file, url, duration) => {
    setUploading(true);
    try {
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const {
        uploadId: multiPartUploadId,
        preSignedUrls,
        format,
      } = await generatePreSignedUrl(file);
      await uploadToPresignedUrl({
        uploadId: multiPartUploadId,
        presignedUrls: preSignedUrls,
        file: file,
        signal: signal,
      });

      const metadata = await extractFileMetadata(file, format);

      const uploadRes = await fetchUploadId({
        url: preSignedUrls[0].url,
        format,
        mimeType: file.type,
        signal,
        metadata,
      });
      handleUpdateImage(uploadRes?.uploadId, url, duration);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message === 'canceled') {
        console.log('Upload aborted');
        return;
      } else {
        console.error(err);
        throw err;
      }
    } finally {
      setUploading(false);
      abortController.current = null;
    }
  };

  const supportedFormats = ['mp4'];

  return (
    <>
      {isFullPage ? (
        <Box>
          {uploadURL ? (
            <Box sx={styles.uploadArea}>
              <CustomPlayer
                url={uploadURL}
                height={isExtraSmall ? '150px' : '376px'}
                autoPlay={true}
              />
              {!uploading && (
                <CloseIcon
                  fontSize={'small'}
                  color="primary"
                  sx={{
                    position: 'absolute',
                    right: '0%',
                    top: '0px',
                    cursor: 'pointer',
                    backgroundColor: (theme) => theme.palette.background.primary,
                    borderRadius: '50%',
                  }}
                  onClick={() => {
                    handleUpdateImage('', '');
                  }}
                />
              )}
            </Box>
          ) : uploading ? (
            <Box sx={styles.uploadArea}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={styles.uploadAreaWrapper}>
              <FileUploader
                multiple={false}
                handleChange={(file) => handleFileSelect(file)}
                name="file"
                types={supportedFormats}
                onTypeError={(err) => {
                  enqueueSnackbar('Unsupported file type. Please upload supported file format', {
                    variant: 'error',
                  });
                }}
                disabled={uploading}
                style={{ width: '100%', height: '100%', textAlign: 'center' }}
              >
                <Box
                  sx={{
                    ...styles.uploadSection,
                    height: isExtraSmall ? '150px' : '350px',
                    width: '100%',
                  }}
                >
                  <Iconify icon="icon-park-outline:upload-one" width="20px" height="20px" />
                  <Typography sx={styles.uploaderText}>Upload Reference Video</Typography>
                  <Typography sx={{ ...styles.uploaderText, fontSize: '10px', mt: '5px' }}>
                    A video of a person performing in the manner that you would like your character
                    to perform.
                  </Typography>
                  <Typography sx={{ ...styles.uploaderText, fontSize: '10px' }}>
                    The video must be between 3 and 30 seconds in duration.
                  </Typography>
                </Box>
              </FileUploader>
            </Box>
          )}

          <Button
            variant="outlined"
            fullWidth
            sx={{
              mt: 1,
              color: 'text.primary',
            }}
            onClick={openBrowseBRollModal}
          >
            Or Select from Media
          </Button>
        </Box>
      ) : (
        <Box mt={1}>
          {!uploadURL && !uploading ? (
            <Box
              sx={{
                ...styles.uploader,
                backgroundColor: uploadBackgroundColor && uploadBackgroundColor,
              }}
            >
              <FileUploader
                multiple={false}
                handleChange={(file) => handleFileSelect(file)}
                name="file"
                types={supportedFormats}
                onTypeError={(err) => {
                  enqueueSnackbar('Unsupported file type. Please upload supported file format', {
                    variant: 'error',
                  });
                }}
                disabled={uploading}
              >
                <Box sx={styles.uploadSection}>
                  <Iconify icon="icon-park-outline:upload-one" width="20px" height="20px" />
                  <Typography sx={styles.uploaderText}>Upload Video</Typography>
                </Box>
              </FileUploader>
              <Box onClick={openBrowseBRollModal} sx={styles.browseMedia}>
                <Typography sx={styles.selecterText}>My Media</Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={styles.uploading}>
              <Box sx={{ borderRadius: '10px' }}>
                <video key={uploadURL} width={'100%'} height={65}>
                  <source src={uploadURL} type="video/mp4" />
                  <source src={uploadURL} type="video/quicktime" />
                  Your browser does not support this video
                </video>
              </Box>
              {uploading && (
                <CircularProgress
                  size={25}
                  sx={{
                    position: 'absolute',
                    left: '40%',
                    top: '40%',
                  }}
                />
              )}
              {!uploading && (
                <CloseIcon
                  fontSize={'small'}
                  color="primary"
                  sx={{
                    position: 'absolute',
                    right: '10%',
                    top: '0px',
                    cursor: 'pointer',
                    backgroundColor: (theme) => theme.palette.background.primary,
                    borderRadius: '50%',
                  }}
                  onClick={() => {
                    handleUpdateImage('', '');
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

const styles: Styles = {
  uploadArea: {
    position: 'relative',
    width: '100%',
    height: { xs: '150px', md: '350px' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    mt: 2,
  },
  uploadAreaWrapper: {
    border: '2px dashed',
    borderColor: (theme) => theme.palette.background.border,
    borderRadius: '12px',
    backgroundColor: (theme) => theme.palette.background.primaryV2,
    textAlign: 'center',
  },
  uploader: {
    height: 100,
    width: 100,
    display: 'flex',
    borderWidth: 1,
    textAlign: 'center',
    overflow: 'hidden',
    borderStyle: 'solid',
    borderRadius: '10px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
  },
  uploadSection: {
    height: 65,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  uploaderText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 500,
  },
  browseMedia: {
    height: 35,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: '#282130',
  },
  selecterText: {
    fontSize: 12,
    fontWeight: 400,
    textDecoration: 'underline',
    color: (theme) => theme.palette.text.coloredText,
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    width: '210px',
  },
};
